import { useSyncExternalStore } from 'react'
import type { TeamPerson } from './api'
import { zoneAt, type World } from './arena'

/**
 * What is going on in the office, in short lines: someone arrives, goes to the kitchen, starts on an
 * item. Worked out in this browser from the difference between two refreshes of who is where, so
 * there is nothing stored on a server and nothing here a person could not see by looking.
 */
export interface ActivityLine {
  id: string
  at: number
  userId: string
  text: string
}

const first = (p: TeamPerson) => p.displayName.split(' ')[0] ?? p.displayName
const SOCIAL = new Set(['kitchen', 'lounge', 'meeting'])
const ROOM_VERB: Record<string, string> = { kitchen: 'is in the', lounge: 'is in the', meeting: 'is in the' }

/** The lines between two snapshots. Nothing at all on the first one: arriving at the page is not news. */
export function activityBetween(prev: readonly TeamPerson[] | null, next: readonly TeamPerson[], world: World, meId: string, at: number): ActivityLine[] {
  if (!prev) return []
  const before = new Map(prev.map((p) => [p.userId, p]))
  const out: ActivityLine[] = []
  const add = (p: TeamPerson, text: string) => out.push({ id: `${p.userId}:${at}:${out.length}`, at, userId: p.userId, text })
  for (const p of next) {
    if (p.userId === meId) continue
    const was = before.get(p.userId)
    if (!was) continue
    if (!was.pos && p.pos) add(p, `${first(p)} arrived at the office`)
    else if (was.pos && !p.pos) add(p, `${first(p)} left the office`)
    else if (was.pos && p.pos) {
      const a = zoneAt(world, was.pos)
      const b = zoneAt(world, p.pos)
      if (a.id !== b.id && SOCIAL.has(b.kind)) add(p, `${first(p)} ${ROOM_VERB[b.kind]} ${b.name}`)
    }
    if (!was.working && p.working) add(p, `${first(p)} started ${p.card?.code ?? 'working'}${p.card ? '' : ' on a private item'}`)
    else if (was.working && !p.working) add(p, `${first(p)} stopped working`)
  }
  return out
}

let lines: ActivityLine[] = []
const listeners = new Set<() => void>()

export function recordActivity(next: readonly ActivityLine[]): void {
  if (next.length === 0) return
  lines = [...lines, ...next].slice(-30)
  listeners.forEach((l) => l())
}

export function useActivity(): ActivityLine[] {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb)
      return () => listeners.delete(cb)
    },
    () => lines,
    () => lines,
  )
}
